import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Text, Badge } from "@radix-ui/themes";
import { PolicyFlowDialog } from "./PolicyFlowDialog";
import { useSimulator } from "../../context/SimulatorWrapper";
import { Tooltip } from "@/components/shared/tooltip";
import styles from "./styles.module.scss";

interface InstructionsSOPVisualizationsProps {
  topicId: number;
}

export function InstructionsSOPVisualizations({
  topicId,
}: InstructionsSOPVisualizationsProps) {
  const { currentRelease } = useSimulator();
  const [selectedDiagram, setSelectedDiagram] = useState<{
    code: string;
    title: string;
  } | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDiagramClick = useCallback(
    (diagram: { code: string; title: string }) => {
      setSelectedDiagram(diagram);
      setIsDialogOpen(true);
    },
    []
  );

  const topicVisualizations = useMemo(() => {
    if (!currentRelease?.topicReleasePolicies) return [];

    const topicPolicy = currentRelease.topicReleasePolicies.find(
      (policy) => policy.topicId === topicId
    );

    if (!topicPolicy?.topicReleasePolicyVisualizations) return [];

    const visualizations: Array<{ id: string; title: string; code: string }> =
      [];

    topicPolicy.topicReleasePolicyVisualizations
      .filter((viz) => viz.generated)
      .forEach((viz) => {
        const fullCode = viz.visualization.join("\n");

        const flowchartMatches = fullCode
          .split(/(?=flowchart TD)/)
          .filter((match) => match.trim());

        if (Array.isArray(flowchartMatches) && flowchartMatches.length > 0) {
          flowchartMatches.forEach((match, index) => {
            const cleanCode = match.trim();
            if (cleanCode) {
              const titleMatch = cleanCode.match(/A\[([^\]]+)\]/);
              let title = titleMatch
                ? titleMatch[1]
                : `Policy Flow ${visualizations.length + 1}`;

              title = title.replace(/^SOP for motion-array — /, "");

              const processedCode = cleanCode;

              visualizations.push({
                id: `viz-${viz.id}-${index}`,
                title: title,
                code: processedCode,
              });
            }
          });
        } else {
          const processedCode = fullCode;

          visualizations.push({
            id: `viz-${viz.id}`,
            title: `Policy Flow ${visualizations.length + 1}`,
            code: processedCode,
          });
        }
      });

    return visualizations;
  }, [currentRelease, topicId]);

  const hasUpdatingSOPs = useMemo(() => {
    if (!currentRelease?.topicReleasePolicies) return false;

    const topicPolicy = currentRelease.topicReleasePolicies.find(
      (policy) => policy.topicId === topicId
    );

    if (!topicPolicy?.topicReleasePolicyVisualizations) return false;

    return topicPolicy.topicReleasePolicyVisualizations.some(
      (viz) => !viz.generated
    );
  }, [currentRelease, topicId]);

  const diagramButtons = useMemo(() => {
    if (topicVisualizations.length === 0) {
      return (
        <Text size="2" color="gray">
          No SOP visualizations available for this topic
        </Text>
      );
    }

    return topicVisualizations.map((diagram) => (
      <Button
        key={diagram.id}
        variant="outline"
        color="gray"
        onClick={() => handleDiagramClick(diagram)}
        className={styles.diagramButton}
      >
        <Flex direction="column" align="start" gap="1">
          <Text size="2" weight="medium">
            {diagram.title}
          </Text>
        </Flex>
      </Button>
    ));
  }, [topicVisualizations, handleDiagramClick]);

  if (topicVisualizations.length === 0 && !hasUpdatingSOPs) {
    return null;
  }

  return (
    <div className={styles.sopVisualizationsSection}>
      <Flex direction="column" gap="3">
        <Flex align="center" gap="2">
          <Text size="3" weight="bold">
            SOP Visualizations
          </Text>
          {hasUpdatingSOPs && (
            <Tooltip>
              <Tooltip.Trigger>
                <Badge color="orange" size="1">
                  Updating Visualizations
                </Badge>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text size="1">
                  We are generating a newer version based on the instructions,
                  briefs and prerequisites being edited
                </Text>
              </Tooltip.Content>
            </Tooltip>
          )}
        </Flex>
        <Text size="2" color="gray">
          Visual representations of standard operating procedures and policy
          flows
        </Text>
        {topicVisualizations.length > 0 && (
          <Flex direction="column" gap="2">
            {diagramButtons}
          </Flex>
        )}
        {topicVisualizations.length === 0 && hasUpdatingSOPs && (
          <Text size="2" color="gray">
            SOP visualizations will appear here once generation is complete
          </Text>
        )}
      </Flex>

      {selectedDiagram && (
        <PolicyFlowDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          mermaidCode={selectedDiagram.code}
          title={selectedDiagram.title}
        />
      )}
    </div>
  );
}
